import { CornerDownRight } from "lucide-react"
import type * as React from "react"
import { Link } from "react-router"

import type { ExpandedLayout } from "@/components/ui/data-table"
import { FEATURES } from "@/lib/features"
import { reviewLandingPath } from "@/lib/roles"
import { cn } from "@/lib/utils"

import { useViewer } from "./hooks/use-viewer"
import { StatusCell, UnreadIcon } from "./queue-cells"
import type { QueueRow, QueueVersion } from "./queue-data"

/*
   The expanded product: one row per real version from `/threads`, newest
   first — as the legacy queue's `VersionRowCells` does. Each version shows its
   own status, its own findings (or that they are not released yet) and its own
   unread count, and links to its own review.

   Every version row uses the table's own column grid (`layout`), so Status,
   Findings and Comms sit exactly under their headers, at every width.
*/

export function ExpandedThread({
  row,
  layout,
}: {
  row: QueueRow
  layout: ExpandedLayout
}): JSX.Element {
  return (
    <ol aria-label={`Versions of ${row.product}`} className="bg-surface-sunken/40">
      {row.versionList.map((version) => (
        <li key={version.reviewId} className="border-t border-border-soft">
          <VersionRow version={version} layout={layout} />
        </li>
      ))}
    </ol>
  )
}

function VersionRow({
  version,
  layout,
}: {
  version: QueueVersion
  layout: ExpandedLayout
}): JSX.Element {
  const cells: Record<string, React.ReactNode> = {
    product: <VersionLabel version={version} />,
    status: <StatusCell status={version.status} stage={version.stage} />,
    findings: <VersionFindings version={version} />,
    // Always the icon on a version row, as legacy does; the bubble only when unread.
    comms: <UnreadIcon unread={version.unread} />,
  }
  return (
    <div
      className={cn("grid items-center gap-3.5 text-body-sm", layout.pad, "py-2")}
      style={{ gridTemplateColumns: layout.template }}
    >
      {Array.from({ length: layout.leadingTracks }, (_, i) => (
        <span key={`lead-${String(i)}`} aria-hidden="true" />
      ))}
      {layout.columns.map((column) => (
        <div
          key={column.id}
          className={cn(
            "min-w-0",
            column.align === "center" && "flex justify-center text-center",
            column.align === "end" && "flex justify-end text-right",
          )}
        >
          {cells[column.id] ?? null}
        </div>
      ))}
    </div>
  )
}

function VersionLabel({ version }: { version: QueueVersion }): JSX.Element {
  const label = `Version ${String(version.versionNo)}`
  // A client opens a review as its report, as everywhere else (#36); staff open the workspace.
  const href = reviewLandingPath(useViewer()?.role, version.reviewId)
  return (
    <div className="flex min-w-0 items-center gap-2 pl-6">
      <CornerDownRight className="size-3.5 shrink-0 text-text-3" aria-hidden="true" />
      {FEATURES.reviewDetail ? (
        <Link
          to={href}
          className={cn(
            "truncate font-medium text-text underline-offset-2 hover:underline",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
          )}
        >
          {label}
        </Link>
      ) : (
        <span className="truncate font-medium text-text">{label}</span>
      )}
      {version.isCurrent && (
        <span className="shrink-0 rounded-full border border-border-soft bg-surface px-1.5 font-id text-caption leading-5 text-text-3">
          Current
        </span>
      )}
    </div>
  )
}

function VersionFindings({ version }: { version: QueueVersion }): JSX.Element {
  if (!version.findingsReleased) {
    return <span className="text-text-3">Findings not released yet</span>
  }
  return (
    <span className="tabular-nums">
      <span className={version.outstanding > 0 ? "text-text" : "text-text-3"}>
        <span className="font-id font-semibold">{version.outstanding}</span> outstanding
      </span>
      {version.corrected > 0 && (
        <span className="text-text-3">
          {" · "}
          <span className="font-id">{version.corrected}</span> corrected
        </span>
      )}
    </span>
  )
}
